let b = 1;
export class Buffer {
    constructor(synth, props) {
        this.synth = synth;
        this.name = (typeof props === "object" && props.name) || "buffer_" + b++;
        const gl = synth.renderer.gl;
        if (typeof props === "number")
            props = { data: props };
        // CASE 2: buffer from numbers -> by default create a Float32Array
        else if (Array.isArray(props))
            props = { data: new Float32Array(props) };
        // CASE 3: buffer from TypedArray
        else if (ArrayBuffer.isView(props))
            props = { data: props };
        //if (!props.buffer && !props.data) throw new Error("buffer needs data or buffer")
        this._usage = typeof props.usage === "string" ? USAGES[props.usage] : props.usage ?? GL_STATIC_DRAW;
        this._target = typeof props.target === "string" ? TARGETS[props.target] : props.target ?? GL_ARRAY_BUFFER;
        this._divisor = props.divisor ?? 0;
        this._stride = props.stride ?? 0;
        this._offset = props.offset ?? 0;
        this._size = props.size ?? 1;
        this._buffer = props.buffer ?? gl.createBuffer();
        if (props.buffer || props.data) {
            const data = props.buffer
                ? webglBufferToTypedArray(gl, props.buffer, (props.type || GL_FLOAT), this._target)
                : props.data;
            const type = WebGLBufferArrayConstructorType[data.constructor.name];
            this.data(data, props.size, type);
        }
        synth.buffers.push(this);
    }
    data(data, size = this._size, type = this._type || GL_FLOAT) {
        const gl = this.synth.renderer.gl;
        if (type && typeof type === "string")
            type = BUFFER_TYPES[type];
        if (Array.isArray(data)) {
            if (!type)
                throw new Error("Buffer type is required when creating a buffer from an array.");
            data = new (glTypeToTypedArray(type))(data);
        }
        type = type || WebGLBufferArrayConstructorType[data.constructor.name];
        this._data = data;
        this._type = bufferType(type);
        this._size = size;
        gl.bindBuffer(this._target, this._buffer);
        gl.bufferData(this._target, this._data, this._usage);
        gl.bindBuffer(this._target, null);
        return this;
    }
    stride(stride, offset = 0) {
        this._stride = stride;
        this._offset = offset;
        return this;
    }
    size(size) {
        this._size = size;
        return this.createData();
    }
    usage(usage) {
        this._usage = typeof usage === "string" ? USAGES[usage] : usage;
        return this._data ? this.data(this._data, this._size, this._type) : this.createData();
    }
    createData() {
        if (this._data || this._buffer)
            return this;
        if (!this._size)
            return this;
        const type = this._type || GL_FLOAT;
        const data = new (glTypeToTypedArray(type))(this._size);
        return this.data(data, this._size, type);
    }
    indices(data, type = GL_UNSIGNED_BYTE) {
        this._target = GL_ELEMENT_ARRAY_BUFFER;
        this._type = bufferType(type);
        return this.data(data, this._size, this._type);
    }
    target(target) {
        this._target = target === "attr" ? GL_ARRAY_BUFFER : target === "indices" ? GL_ELEMENT_ARRAY_BUFFER : target;
        return this._data ? this.data(this._data, this._size, this._type) : this;
    }
    divisor(divisor) {
        this._divisor = divisor;
        return this;
    }
    type(type) {
        if (typeof type === "undefined") {
            if (!this._data)
                throw new Error("Buffer type is undefined and no data is set");
            type = WebGLBufferArrayConstructorType[this._data.constructor.name];
        }
        this._type = bufferType(type);
        return this._data ? this.data(this._data, this._size, this._type) : this;
    }
    clear() {
        const gl = this.synth.renderer.gl;
        gl.deleteBuffer(this._buffer);
        this._data = null;
        // remove from synth
        const index = this.synth.buffers.indexOf(this);
        if (index !== -1) {
            this.synth.buffers.splice(index, 1);
        }
    }
    async print() {
        const data = await this.read();
        console.log(this.name, data);
    }
    async read(store = false) {
        const gl = this.synth.renderer.gl;
        return new Promise(resolve => {
            const sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
            const checkStatus = () => {
                const status = gl.clientWaitSync(sync, gl.SYNC_FLUSH_COMMANDS_BIT, 0);
                if (status === gl.TIMEOUT_EXPIRED) {
                    //console.log("GPU is still busy")
                    setTimeout(checkStatus);
                }
                else if (status === gl.WAIT_FAILED) {
                    console.error("Something went wrong with the sync object");
                }
                else {
                    const result = webglBufferToTypedArray(gl, this._buffer, this._type, this._target);
                    if (store)
                        this._data = result;
                    resolve(result);
                }
            };
            checkStatus();
        });
        //return webglBufferToTypedArray(gl, b.buffer, b.type as WebGLBufferTypeArray, b.target)
    }
    clone() {
        return new Buffer(this.synth, {
            name: this.name + "_clone",
            buffer: this._buffer,
            data: this._data,
            type: this._type,
            size: this._size,
            target: this._target,
            usage: this._usage,
            divisor: this._divisor,
        });
    }
}
// Constants
export const GL_ARRAY_BUFFER = 0x8892;
export const GL_ELEMENT_ARRAY_BUFFER = 0x8893;
export const GL_BYTE = 0x1400;
export const GL_UNSIGNED_BYTE = 0x1401;
export const GL_SHORT = 0x1402;
export const GL_UNSIGNED_SHORT = 0x1403;
export const GL_INT = 0x1404;
export const GL_UNSIGNED_INT = 0x1405;
export const GL_FLOAT = 0x1406;
export const GL_STATIC_DRAW = 0x88e4;
export const GL_DYNAMIC_DRAW = 0x88e8;
export const GL_STREAM_DRAW = 0x88e0;
export const GL_STREAM_READ = 0x88e1;
export const GL_STREAM_COPY = 0x88e2;
export const GL_STATIC_READ = 0x88e5;
export const GL_STATIC_COPY = 0x88e6;
export const GL_DYNAMIC_READ = 0x88e9; // <-- Quella che ti serviva
export const GL_DYNAMIC_COPY = 0x88ea;
export const GL_INTERLEAVED_ATTRIBS = 0x8c8c;
export const GL_SEPARATE_ATTRIBS = 0x8c8d;
// Mappers
const USAGES = {
    // Draw
    static: GL_STATIC_DRAW,
    dynamic: GL_DYNAMIC_DRAW,
    stream: GL_STREAM_DRAW,
    // Read
    "static read": GL_STATIC_READ,
    "dynamic read": GL_DYNAMIC_READ,
    "stream read": GL_STREAM_READ,
    // Copy
    "static copy": GL_STATIC_COPY,
    "dynamic copy": GL_DYNAMIC_COPY,
    "stream copy": GL_STREAM_COPY,
};
const TARGETS = {
    attr: GL_ARRAY_BUFFER,
    indices: GL_ELEMENT_ARRAY_BUFFER,
};
export const BUFFER_TYPES = {
    byte: GL_BYTE,
    ubyte: GL_UNSIGNED_BYTE,
    short: GL_SHORT,
    ushort: GL_UNSIGNED_SHORT,
    int: GL_INT,
    uint: GL_UNSIGNED_INT,
    float: GL_FLOAT,
};
const BUFFER_TYPES_REVERSE = Object.fromEntries(Object.entries(BUFFER_TYPES).map(([k, v]) => [v, k]));
export function bufferTypeName(type) {
    if (typeof type === "string")
        return type;
    return BUFFER_TYPES_REVERSE[type];
}
export function bufferType(type) {
    if (typeof type === "string")
        return BUFFER_TYPES[type];
    return type;
}
export const WebGLBufferTypeArrayConstructor = {
    [GL_BYTE /* 5120 */]: Int8Array,
    [GL_UNSIGNED_BYTE /* 5121 */]: Uint8Array,
    [GL_SHORT /* 5122 */]: Int16Array,
    [GL_UNSIGNED_SHORT /* 5123 */]: Uint16Array,
    [GL_INT /* 5124 */]: Int32Array,
    [GL_UNSIGNED_INT /* 5125 */]: Uint32Array,
    [GL_FLOAT /* 5126 */]: Float32Array,
};
const WebGLBufferArrayConstructorType = {
    Int8Array: GL_BYTE,
    Uint8Array: GL_UNSIGNED_BYTE,
    Int16Array: GL_SHORT,
    Uint16Array: GL_UNSIGNED_SHORT,
    Int32Array: GL_INT,
    Uint32Array: GL_UNSIGNED_INT,
    Float32Array: GL_FLOAT,
    Array: GL_FLOAT,
};
// Utilties
// Given a GL type return the TypedArray needed
export function glTypeToTypedArray(type) {
    if (!(type in WebGLBufferTypeArrayConstructor))
        throw new Error(`glTypeToTypedArray no key: ${type}`);
    return WebGLBufferTypeArrayConstructor[type];
}
export function webglBufferToTypedArray(gl, buffer, type, target = gl.ARRAY_BUFFER) {
    const TypedArray = glTypeToTypedArray(type);
    gl.bindBuffer(target, buffer);
    const bufferSize = gl.getBufferParameter(target, gl.BUFFER_SIZE);
    const array = new TypedArray(bufferSize / TypedArray.BYTES_PER_ELEMENT);
    gl.getBufferSubData(target, 0, array);
    gl.bindBuffer(target, null);
    return array;
}
